/**
 * Axios HTTP client configured for AI-Tutor backend API
 * Includes interceptors for authentication and error handling
 *
 * BUG-20260123-235853: Updated error handling to differentiate network vs server errors
 * Deployment trigger: Ensure VITE_API_URL is set from CloudFormation during GitHub Actions build
 */

import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import type { AuthError } from '@/types/auth';
// google-sign-in/US-004 (T5): api.ts is a module (not a component), so the
// consent-gate flag is set via getState() — the same technique useAuth.ts
// uses for useAuthStore.getState() outside a component render.
import { useConsentGateStore } from '@/store/consentGate';

// API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Create axios instance with defaults
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor: Add auth token if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage (will be managed by useAuth hook)
    const idToken = localStorage.getItem('idToken');

    if (idToken && config.headers) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Transform errors to AuthError format
// BUG-20260123-235853: Differentiate between actual network errors and server errors - 2026-01-24
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string; details?: Record<string, string> }>) => {
    // BUG-20260123-235853: Check if this is a true network error (no response at all)
    // vs a server error that returned a response (like 500)
    if (!error.response) {
      // BUG-20260702-121632: A responseless error is NOT always "no internet connection" —
      // it can also be a request timeout, or a CORS-blocked / unreachable-route failure
      // while the client is genuinely online (the browser hides the response in that case,
      // so error.response is undefined here too). Categorise honestly instead of always
      // blaming the user's connection (see TO-LL-002). code stays 'NETWORK_ERROR' in all
      // three branches — it is relied on by systemErrorCodes, useStartSession,
      // useMathCoachMessages, and ResetChildPasswordDialog.
      let message: string;
      if (error.code === 'ECONNABORTED') {
        // Axios sets ECONNABORTED for client-side request timeouts
        message = 'The request timed out. Please try again.';
      } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        // Genuinely offline - the original, accurate wording
        message = 'Unable to connect to server. Please check your internet connection.';
      } else {
        // Online but no response reached the app (e.g. server unreachable, blocked
        // route, or CORS failure) - do not falsely blame the user's internet
        message =
          'We could not reach the server. This may be a temporary problem on our end. Please try again.';
      }

      const authError: AuthError = {
        code: 'NETWORK_ERROR',
        message,
      };
      return Promise.reject(authError);
    }

    // Transform backend error to AuthError
    const { status, data } = error.response;

    // google-sign-in/US-004 (T5) / AC-004.1, AC-004.6: detection keyed
    // STRICTLY on status 403 AND data.error === 'CONSENT_REQUIRED' — never
    // on a status-only 403, so a plain 403 FORBIDDEN (e.g. require_group
    // rejecting a student) never opens the consent modal. Side-effect only:
    // the error is still rejected below exactly as before (no swallow) so
    // every existing caller's error handling is unchanged. Enforcement of
    // the gate itself is entirely server-side (TS-004's require_consent);
    // this flag is UI convenience only (AC-004.6).
    if (status === 403 && data?.error === 'CONSENT_REQUIRED') {
      useConsentGateStore.getState().setConsentRequired(true);
    }

    // BUG-20260123-235853: For server errors (5xx), use appropriate message
    // If the server responded but with an error, it's not a network issue
    const authError: AuthError = {
      code: data?.error || mapStatusToCode(status),
      message: data?.message || getDefaultMessage(status),
      details: data?.details,
    };

    return Promise.reject(authError);
  }
);

/**
 * Map HTTP status codes to error codes
 */
function mapStatusToCode(status: number): string {
  switch (status) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'RATE_LIMITED';
    case 500:
    case 503:
      return 'SERVER_ERROR';
    default:
      return 'SERVER_ERROR';
  }
}

/**
 * Get default error message for status code
 */
function getDefaultMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Please log in to continue.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 409:
      return 'This operation conflicts with existing data.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Something went wrong. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred.';
  }
}

export default api;
